import {Group} from '@visx/group';
import Pie, {PieArcDatum, ProvidedProps} from '@visx/shape/lib/shapes/Pie';
import {css, StyleSheet} from 'aphrodite';
import React, {useState, useRef} from 'react';

import {DataFrame, DataType, DataFrameRow} from 'data-frames/DataFrameTypes';
import {
  ColumnsMapping,
  ColumnValidationMap,
  numericAccessor,
  resolvedStringAccessor,
} from 'data-frames/util/AccessorUtils';
import {createAccessors} from 'data-frames/util/createAccessors';
import findAndAssertColumn from 'data-frames/util/findAndAssertColumn';
import {ColumnTypeValidationFlag} from 'data-frames/util/validateColumnType';
import {LeafType} from 'data-types/benchling-type-def/BenchlingTypeDef';
import {AnalyticsVisualizationColorPalette as ColorPalette} from 'graphql-client/__generated/graphqlGlobalTypes';
import {VISUALIZATION_RESOLUTION_CONFIG} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import ChartTooltip, {useChartTooltip} from 'ui-library/charting/internals/ChartTooltip';
import {LabelCompatibleType} from 'ui-library/charting/Types';
import prettyChartNumber from 'ui-library/charting/util/prettyChartNumber';
import {JSONValue} from 'util/Types';

import BenchlingChart from './internals/BenchlingChart';
import DiscreteSeriesLegend, {estimateDiscreteSeriesLegendWidth} from './internals/DiscreteSeriesLegend';
import {useSeriesExcludedWithDataFrameState} from './internals/hooks/useSeriesExcludedWithDataFrameState';
import {LegendItemDirection, LegendLocation} from './internals/LegendConstants';
import PieChartSlice from './internals/PieChartSlice';
import getChartDimensions from './internals/utils/getChartDimensions';
import {getDiscreteColorScale} from './internals/utils/getSeriesColorConfig';
import {DEFAULT_CHART_MARGIN, LABEL_HEIGHT} from './internals/utils/Theme';

export const LABEL_PADDING = 13;
const LEGEND_WIDTH_PERCENTAGE = 0.33;

type PieChartColumns = 'sliceName' | 'measure';

export const PIE_CHART_VALIDATION_MAP: ColumnValidationMap<PieChartColumns> = {
  measure: {
    allowedLeafTypes: [LeafType.DECIMAL, LeafType.INTEGER, LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.NOT_ALLOWED,
  },
  sliceName: {
    allowedLeafTypes: [LeafType.DECIMAL, LeafType.INTEGER, LeafType.TEXT],
    nullableType: ColumnTypeValidationFlag.NOT_ALLOWED,
    listType: ColumnTypeValidationFlag.NOT_ALLOWED,
    maybeTextType: ColumnTypeValidationFlag.ALLOWED,
  },
};

export interface PieChartConfig {
  sliceLabels: {
    showPercentage: boolean;
    showSliceName: boolean;
    showValue: boolean;
  };
  legend?: {
    location: LegendLocation;
  };
}

export type PieChartColumnsMapping = ColumnsMapping<PieChartColumns>;

export function getNumericLabel(
  value: number,
  totalPieValue: number,
  showValue: boolean,
  showPercentage: boolean
): string {
  const percentage = (value / totalPieValue) * 100;

  if (showValue && showPercentage) {
    return `${prettyChartNumber(value)} (${Math.round(percentage)}%)`;
  } else if (showValue) {
    return prettyChartNumber(value);
  } else if (showPercentage) {
    return `${Math.round(percentage)}%`;
  }

  return '';
}

export interface PieChartProps {
  columnsMapping: PieChartColumnsMapping;
  config: PieChartConfig;
  dataFrame: DataFrame;
}

export function PieChart(props: PieChartProps & {height: number; width: number}): JSX.Element {
  const [hoveredSliceName, setHoveredSliceName] = useState<JSONValue>(null);
  const pieReference = useRef<ProvidedProps<DataFrameRow> | null>(null);

  const {
    columnsMapping,
    config: {legend},
    dataFrame,
    height,
    width,
  } = props;
  const {accessors, validatedLeafTypes} = createAccessors(
    dataFrame,
    columnsMapping,
    PIE_CHART_VALIDATION_MAP
  );
  const measureColumn = findAndAssertColumn(dataFrame, columnsMapping.measure);
  const sliceNameAccessor = resolvedStringAccessor(
    accessors.sliceName,
    dataFrame.hydratedProperties,
    VISUALIZATION_RESOLUTION_CONFIG
  );
  const valueAccessor = numericAccessor(accessors.measure, validatedLeafTypes.measure);

  const {tooltipProps, updateTooltipProps} = useChartTooltip<DataFrameRow>({
    axisTypes: {
      // We pre-format the measure as "{value} ({percentage}%)", so we want to type as STRING here so that the
      // chart tooltip doesn't attempt to deserialize the passed string as a float.
      [columnsMapping.measure]: DataType.STRING,
    },
    columnLabels: {
      [columnsMapping.measure]: measureColumn.displayName,
    },
    columnNames: [columnsMapping.measure],
  });

  const {
    excludedSeries: excludedSlices,
    filteredDataFrame,
    toggleExcludedSeries: toggleExcludedSlice,
  } = useSeriesExcludedWithDataFrameState(dataFrame, sliceNameAccessor);

  const showLegend = legend != null;
  const colorScale = getDiscreteColorScale(dataFrame.rows.map((point) => sliceNameAccessor(point)));
  const legendWidth = showLegend
    ? estimateDiscreteSeriesLegendWidth(colorScale, LEGEND_WIDTH_PERCENTAGE * width)
    : 0;
  const svgWidth = width - legendWidth;
  const svgHeight = height;

  const {chartWidth, chartHeight} = getChartDimensions(
    {
      top: DEFAULT_CHART_MARGIN + LABEL_HEIGHT,
      right: DEFAULT_CHART_MARGIN,
      bottom: DEFAULT_CHART_MARGIN + LABEL_HEIGHT,
      left: DEFAULT_CHART_MARGIN,
    },
    svgWidth,
    svgHeight
  );
  const radius = Math.min(chartWidth, chartHeight) / 2 - LABEL_PADDING;
  const centerY = svgHeight / 2;
  const centerX = svgWidth / 2;
  const totalPieValue = filteredDataFrame.rows.reduce((sum, point) => sum + valueAccessor(point), 0);

  function onHoverSlice(pie: ProvidedProps<DataFrameRow>, arc?: PieArcDatum<DataFrameRow>): void {
    if (arc != null) {
      const [centroidX, centroidY] = pie.path.centroid(arc);

      setHoveredSliceName(sliceNameAccessor(arc.data));
      updateTooltipProps({
        top: centroidY + centerY,
        label: {
          color: colorScale(sliceNameAccessor(arc.data)),
          text: sliceNameAccessor(arc.data),
        },
        left: centroidX + centerX,
        offsetTop: 0,
        offsetLeft: 10,
        point: {
          [columnsMapping.measure]: getNumericLabel(valueAccessor(arc.data), totalPieValue, true, true),
        },
      });
    } else {
      setHoveredSliceName(null);
      updateTooltipProps(null);
    }
  }

  function onHoverLegendItem(label?: LabelCompatibleType): void {
    if (pieReference.current == null) {
      return;
    }

    const arc = pieReference.current.arcs.find((arc) => sliceNameAccessor(arc.data) === label);
    onHoverSlice(pieReference.current, arc ?? undefined);
  }

  const legendElement = legend != null && (
    <DiscreteSeriesLegend
      colorConfig={{
        colorScale,
        colorPalette: ColorPalette.DISCRETE,
      }}
      width={legendWidth}
      height={height}
      direction={LegendItemDirection.COLUMN}
      location={legend.location}
      excludedSeries={excludedSlices}
      onClickLegendItem={toggleExcludedSlice}
      onHoverItem={onHoverLegendItem}
    />
  );

  function estimateMaxLabelLength(labelX: number): number {
    const availableSpace = svgWidth / 2 - Math.abs(labelX);

    // This is a rough estimation that fits a line based on observations at different window sizes.
    return 0.169 * availableSpace - 1.25;
  }

  return (
    <div className={css(styles.chartContainer)}>
      {showLegend && legend?.location === LegendLocation.LEFT && legendElement}
      <svg width={svgWidth} height={svgHeight}>
        {tooltipProps && <ChartTooltip {...tooltipProps} />}
        <Group top={centerY} left={centerX}>
          <Pie data={filteredDataFrame.rows} pieValue={valueAccessor} outerRadius={radius}>
            {(pie) => {
              pieReference.current = pie;

              return pie.arcs.map((arc, index) => {
                const isHovered = hoveredSliceName === sliceNameAccessor(arc.data);

                return (
                  <PieChartSlice
                    key={`arc-${sliceNameAccessor(arc.data)}-${index}`}
                    arc={arc}
                    color={colorScale(sliceNameAccessor(arc.data))}
                    config={props.config}
                    getNumericLabel={getNumericLabel}
                    estimateMaxLabelLength={estimateMaxLabelLength}
                    isFaded={hoveredSliceName != null && !isHovered}
                    isHovered={isHovered}
                    onHover={(arc: PieArcDatum<DataFrameRow>) => {
                      onHoverSlice(pie, arc);
                    }}
                    pie={pie}
                    sliceNameAccessor={sliceNameAccessor}
                    totalPieValue={totalPieValue}
                    valueAccessor={valueAccessor}
                  />
                );
              });
            }}
          </Pie>
        </Group>
      </svg>
      {showLegend && legend?.location === LegendLocation.RIGHT && legendElement}
    </div>
  );
}

export default function PieChartWrapper(props: Omit<PieChartProps, 'height' | 'width'>): JSX.Element {
  return (
    <BenchlingChart dataTestAttrs={{component: 'PieChart', element: 'root'}}>
      {({height, width}) => <PieChart {...props} height={height} width={width} />}
    </BenchlingChart>
  );
}

const styles = StyleSheet.create({
  chartContainer: {
    display: 'flex',
    flexDirection: 'row',
  },
});
